import { lbBrand } from '@fuse/colors';
import {
  AppBar,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  makeStyles,
  Select,
  TextField,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { CURRENCY } from 'app/constants/currency.constant';
import { STORAGE_KEYS } from 'app/constants/storage.constant';
import { useStores } from 'app/hooks/useStores';
import { getFranchiseTagsApi } from 'app/main/apps/franchises/api';
import { selectUserFranchise } from 'app/main/apps/franchises/ducks/franchises.selector';
import { LBOrderDTO } from 'app/main/apps/scanner/types';
import { injectPointsMultipleTagsApi, resetPointsInTagApi } from 'app/main/apps/stores/api';
import DialogCloseButton from 'app/main/design-system/components/DialogCloseButton';
import { showMessage } from 'app/store/fuse/messageSlice';
import { checkFloatText } from 'app/utils/string.utils';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TagItemInDTO } from '../../type';
import { SaleRow } from './SalesList';

interface CreateSaleDialogProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles(theme => ({
  select: {
    backgroundColor: 'white',
    width: '100%',
    '& [class*="MuiInputLabel-outlined"][data-shrink="true"]': {
      transform: 'translate(14px, -6px) scale(0.55) !important',
    },
  },
  amountInput: {
    '& [class*="MuiInput-underline"]:before': {
      borderBottom: `1px solid ${theme.palette.primary.main} !important`,
    },
    '& [class*="MuiInput-underline"]:hover:not(.Mui-disabled):before': {
      borderBottom: `1px solid ${theme.palette.primary.main} !important`,
    },
    '& input[type="number"]::-webkit-inner-spin-button, input[type="number"]::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '& input[type="number"]': {
      MozAppearance: 'textfield',
    },
  },
}));

const CreateSaleDialog: React.FC<CreateSaleDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const franchise = useSelector(selectUserFranchise);

  const [tags, setTags] = useState<TagItemInDTO[]>([]);
  const [lastSale, setLastSale] = useState<LBOrderDTO | null>(null);

  const { currentStoreId } = useStores();

  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(() => {
    const localData = localStorage.getItem(STORAGE_KEYS.SELECTED_TAG_ID);
    try {
      const parsed = localData ? JSON.parse(localData) : [];
      if (!Array.isArray(parsed)) {
        localStorage.removeItem(STORAGE_KEYS.SELECTED_TAG_ID);
        return [];
      }
      return parsed;
    } catch {
      localStorage.removeItem(STORAGE_KEYS.SELECTED_TAG_ID);
      return [];
    }
  });

  const [amount, setAmount] = useState('');

  const amountRef = useRef<HTMLInputElement>(null);

  const fetchTags = async () => {
    try {
      setTags([]);
      const response = await getFranchiseTagsApi(franchise?.id, currentStoreId);
      setTags(response.data);
      if (response.data.length === 1) {
        const { tagId } = response.data[0];
        setSelectedTagIds([tagId]);
        localStorage.setItem(STORAGE_KEYS.SELECTED_TAG_ID, JSON.stringify([tagId]));
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        dispatch(showMessage({ message: t('dialogs.createSaleDialog.fetchTagsError'), variant: 'error' }));
      } else if (open) {
        dispatch(showMessage({ message: t('dialogs.createSaleDialog.noTagsFound'), variant: 'info' }));
      }
    }
  };

  const injectPointsInTag = async () => {
    try {
      const response = await injectPointsMultipleTagsApi(selectedTagIds, Number(amount));
      setLastSale(response);
      setAmount('');
    } catch (_error) {
      dispatch(showMessage({ message: t('dialogs.createSaleDialog.injectPointsError'), variant: 'error' }));
    }
  };

  const resetPointsInTag = async () => {
    try {
      const promises = selectedTagIds.map(tagId => resetPointsInTagApi(tagId));
      await Promise.all(promises);
      setLastSale(null);
    } catch (_error) {
      dispatch(showMessage({ message: t('dialogs.createSaleDialog.resetTagError'), variant: 'error' }));
    }
  };

  const handleChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (!value.length || checkFloatText(value, false)) {
      setAmount(value);
    }
  };

  const handleSelectTag = (tagIds: string[]) => {
    setSelectedTagIds(tagIds);
    localStorage.setItem(STORAGE_KEYS.SELECTED_TAG_ID, JSON.stringify(tagIds));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation_todo>
  useEffect(() => {
    if (open && currentStoreId) {
      fetchTags();
    }
  }, [open, currentStoreId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      keepMounted
      className="m-8 sm:m-24"
      PaperProps={{ className: 'rounded-8' }}
      aria-modal="true"
    >
      <AppBar position="static" elevation={0}>
        <Toolbar className="px-24 flex justify-between">
          <Typography variant="h6" color="inherit">
            {t('dialogs.createSaleDialog.dialogTitle')}
          </Typography>
          <DialogCloseButton onClose={onClose} />
        </Toolbar>
      </AppBar>
      <DialogContent className="m-0 px-0 py-24 sm:px-24 flex flex-col items-center">
        <Card elevation={0} className="w-full overflow-scroll">
          <CardHeader
            className="text-center"
            title={t('dialogs.createSaleDialog.title')}
            titleTypographyProps={{ variant: 'h6' }}
            subheader={t('dialogs.createSaleDialog.subtitle')}
          />
          <CardContent className="flex flex-1 flex-col gap-16">
            <div>
              <Typography color="textSecondary" variant="subtitle2" className="mb-16">
                {t('dialogs.createSaleDialog.amountOfSale')}
              </Typography>
              <div
                className="w-full flex justify-center border-gray-400 border-1 rounded-8 p-32"
                onClick={() => amountRef.current?.focus()}
              >
                <TextField
                  type="number"
                  className={classes.amountInput}
                  value={amount}
                  placeholder="0.00"
                  inputProps={{
                    className: 'text-5xl font-bold w-192',
                    style: { color: lbBrand.red },
                    inputMode: 'decimal',
                  }}
                  inputRef={amountRef}
                  onChange={handleChangeAmount}
                  onKeyDown={e => e.key === 'Enter' && injectPointsInTag()}
                />
                <Typography color="primary" className="ml-8 text-5xl font-bold">
                  {CURRENCY[franchise?.currencyCode]?.symbol_native}
                </Typography>
              </div>
            </div>

            <div>
              <Typography color="textSecondary" variant="subtitle2" className="mb-16">
                {t('dialogs.createSaleDialog.selectTagInfoText')}
              </Typography>

              <FormControl
                variant="outlined"
                className={clsx('rounded-8', classes.select)}
                disabled={!currentStoreId || !tags.length}
              >
                <InputLabel id="tag-selector-label" className="text-xl">
                  {t('basic.fields.tags')}
                </InputLabel>
                <Select
                  id="tag-selector"
                  multiple
                  label={t('basic.fields.tags')}
                  labelWidth={100}
                  labelId="tag-selector-label"
                  inputProps={{ className: 'flex items-center text-xl' }}
                  onChange={event => handleSelectTag(event.target.value as string[])}
                  value={selectedTagIds.length && tags.length ? selectedTagIds : []}
                  renderValue={selected =>
                    (selected as string[])
                      .map(tagId => {
                        const tag = tags.find(selectedTag => selectedTag.tagId === tagId);
                        return tag?.name ? `${tag.name} - ${tagId}` : tagId;
                      })
                      .join(', ')
                  }
                >
                  {tags.map(tag => (
                    <MenuItem key={tag.id} value={tag.tagId}>
                      <Checkbox checked={selectedTagIds.includes(tag.tagId)} />
                      {tag.name ? `${tag.name} - ${tag.tagId}` : tag.tagId}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <Button
              className="w-full py-16"
              variant="contained"
              size="large"
              color="primary"
              onClick={injectPointsInTag}
              disabled={!selectedTagIds.length || !Number(amount)}
            >
              <Typography variant="h6" color="inherit">
                {t('basic.actions.valid')}
              </Typography>
            </Button>

            {lastSale && (
              <div className="w-full">
                <Typography color="textSecondary" variant="subtitle2" className="mb-16">
                  {t('dialogs.createSaleDialog.lastSale')}
                </Typography>
                <SaleRow
                  sale={lastSale}
                  currency={CURRENCY[franchise?.currencyCode]?.symbol_native}
                  onResetPointsInTag={resetPointsInTag}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSaleDialog;
